import React, { useState } from 'react';
import { ArrowLeft, ChevronDown, ChevronRight } from 'lucide-react';
import { getTimeLeft, getLeaseDuration } from './fleetConstants';

function FleetDetailPanel({ selectedLane, storeIss, currentUser, onRelease, onAllocate, onExtend, onClose, actionLoading }) {
  const [detailsExpanded, setDetailsExpanded] = useState(true);
  const [purpose, setPurpose] = useState('');
  const [duration, setDuration] = useState('');
  const [durationError, setDurationError] = useState('');

  const handleAllocate = () => {
    let durationSeconds = null;
    if (duration) {
      const trimmed = duration.trim().toLowerCase();
      let minutes;
      if (trimmed.endsWith('h')) {
        minutes = parseFloat(trimmed) * 60;
      } else if (trimmed.endsWith('m')) {
        minutes = parseFloat(trimmed);
      } else {
        minutes = parseFloat(trimmed);
      }
      if (isNaN(minutes) || minutes < 30 || minutes > 90) {
        setDurationError('Duration must be between 30m and 90m');
        return;
      }
      durationSeconds = minutes * 60;
    }
    setDurationError('');
    onAllocate(selectedLane.laneId, purpose, durationSeconds);
    setPurpose('');
    setDuration('');
  };

  return (
    <div className="fleet-detail-panel">
      <button className="fleet-detail-close" onClick={onClose}>
        <ArrowLeft size={16} /> Back
      </button>
      <h3>{selectedLane.hardwareId || selectedLane.laneId}</h3>
      <div className={`fleet-detail-status ${selectedLane.status === 'FREE' ? 'detail-free' : 'detail-busy'}`}>
        {selectedLane.status === 'FREE' ? '🟢 Available' : '🟠 In Use'}
      </div>
      <div className="fleet-detail-toggle" onClick={() => setDetailsExpanded(prev => !prev)}>
        {detailsExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <span>Lane Details</span>
      </div>
      {detailsExpanded && (
      <table className="fleet-detail-table">
        <tbody>
          <tr><td>Type</td><td>{selectedLane.laneType}</td></tr>
          <tr><td>Stage</td><td>{selectedLane.deploymentStage || '—'}</td></tr>
          <tr><td>Device ID</td><td>{selectedLane.deviceId || '—'}</td></tr>
          <tr><td>IP Address</td><td>{selectedLane.ipAddress || '—'}</td></tr>
          <tr><td>Port</td><td>{selectedLane.port || '—'}</td></tr>
          <tr><td>DSN</td><td>{selectedLane.dsn || '—'}</td></tr>
          <tr><td>Peripherals</td><td>{selectedLane.peripherals || '—'}</td></tr>
          <tr><td>Enterprise</td><td>{selectedLane.enterpriseConfig || '—'}</td></tr>
          {selectedLane.enterpriseUrl && (
            <tr><td>Enterprise URL</td><td><a href={selectedLane.enterpriseUrl} target="_blank" rel="noreferrer">{selectedLane.enterpriseUrl}</a></td></tr>
          )}
          {storeIss && (
            <tr><td>ISS</td><td><a href={storeIss.webUiUrl} target="_blank" rel="noreferrer">{storeIss.ipAddress}:{storeIss.port}</a></td></tr>
          )}
          {selectedLane.enterpriseUrl && (
            <tr><td>Admin UI</td><td><a href={selectedLane.enterpriseUrl + '/admin-ui/'} target="_blank" rel="noreferrer">Open Admin UI</a></td></tr>
          )}
          {selectedLane.webUiUrl && (
            <tr><td>Web UI</td><td><a href={selectedLane.webUiUrl} target="_blank" rel="noreferrer">Open POS UI</a></td></tr>
          )}
          <tr><td>Lab</td><td>{selectedLane.lab || '—'}</td></tr>
          {(selectedLane.gammaLibby || storeIss?.gammaLibby) && (
            <tr><td>Libby ID</td><td>{selectedLane.gammaLibby || storeIss?.gammaLibby}</td></tr>
          )}
          {selectedLane.status === 'IN_USE' && (
            <>
              <tr><td>Allocated By</td><td><strong>{selectedLane.allocatedBy}</strong></td></tr>
              <tr><td>Since</td><td>{new Date(selectedLane.allocatedAt).toLocaleString()}</td></tr>
              <tr><td>Purpose</td><td>{selectedLane.purpose || '—'}</td></tr>
            </>
          )}
          {selectedLane.lastReleasedBy && selectedLane.status === 'FREE' && (
            <tr><td>Last Used By</td><td>{selectedLane.lastReleasedBy} ({new Date(selectedLane.lastReleasedAt).toLocaleString()})</td></tr>
          )}
        </tbody>
      </table>
      )}

      {selectedLane.status === 'FREE' && (
        <div className="fleet-action">
          <input
            type="text"
            placeholder="Purpose (e.g., Testing BRD-1234 tender flows)"
            value={purpose}
            onChange={e => setPurpose(e.target.value)}
            className="fleet-purpose-input"
          />
          <input
            type="text"
            placeholder="Duration (optional, e.g. 30m, 1h, 1.5h)"
            value={duration}
            onChange={e => { setDuration(e.target.value); setDurationError(''); }}
            className="fleet-purpose-input"
          />
          {durationError && <span className="fleet-duration-error">{durationError}</span>}
          <button
            className="btn btn-primary"
            onClick={handleAllocate}
            disabled={actionLoading}
          >
            {actionLoading ? 'Allocating...' : 'Allocate Lane (Default 30m)'}
          </button>
        </div>
      )}

      {selectedLane.status === 'IN_USE' && (selectedLane.allocatedBy === currentUser || selectedLane.allocatedBy === currentUser + '@amazon.com') && (
        <div className="fleet-action">
          {selectedLane.allocatedAt && getTimeLeft(selectedLane.allocatedAt, selectedLane.leaseDuration) !== 'expired' && (
            <div className="fleet-timer-display">⏱ {getTimeLeft(selectedLane.allocatedAt, selectedLane.leaseDuration)}</div>
          )}
          {selectedLane.allocatedAt && (() => {
            const remaining = getLeaseDuration(selectedLane) - (Date.now() - new Date(selectedLane.allocatedAt).getTime());
            return remaining < getLeaseDuration(selectedLane) * 0.7 && remaining > 0;
          })() && (
            <div className="fleet-extend-options">
              <span className="fleet-extend-label">Extend for:</span>
              <button className="btn btn-sm btn-primary" onClick={() => onExtend(selectedLane.laneId, 1800)} disabled={actionLoading}>30m</button>
              <button className="btn btn-sm btn-primary" onClick={() => onExtend(selectedLane.laneId, 3600)} disabled={actionLoading}>1h</button>
              <button className="btn btn-sm btn-primary" onClick={() => onExtend(selectedLane.laneId, 7200)} disabled={actionLoading}>2h</button>
            </div>
          )}
          <button
            className="btn btn-danger"
            onClick={() => onRelease(selectedLane.laneId)}
            disabled={actionLoading}
          >
            {actionLoading ? 'Releasing...' : 'Release Lane'}
          </button>
        </div>
      )}
    </div>
  );
}

export default FleetDetailPanel;
